import { api } from '../../lib/api.js';
import { authState } from '../../lib/auth.js';
import { BaseComponent } from '../../lib/base-component.js';
import {
    ITEM_FILTERS,
    isClaimActive,
    itemMatches,
    moveId,
    OUTCOMES,
    outcomeOf,
    sortStalestFirst,
    splitItemLines,
    stalenessOf,
    timeAgo,
} from '../../lib/et.js';
import { css, html, nothing } from '../../lib/lit.js';
import { router } from '../../lib/router.js';

// One exploratory-testing plan: its sections and one-line items, each with when
// it was last tested and how, what issues it has, and who has claimed which
// section. Testers record a result inline; edit mode builds the plan itself.
//
// Every change reloads the plan rather than patching it locally: the server is
// where latest results, claims and issue states are worked out, and agents may
// be editing the same plan over MCP.
class ETPlanView extends BaseComponent {
    static properties = {
        planId: { type: String, attribute: 'plan-id' },
        /** @type {object|null} */
        _plan: { state: true },
        _loading: { state: true },
        _error: { state: true },
        _filter: { state: true },
        _stalestFirst: { state: true },
        _editing: { state: true },
        /** @type {{itemId: string, outcome: string}|null} */
        _recording: { state: true },
        _note: { state: true },
        _build: { state: true },
        /** The item a fail or block was just recorded on, to offer filing an issue. */
        _justFailed: { state: true },
        _busy: { state: true },
        _actionError: { state: true },
        _newSection: { state: true },
        /** @type {Record<string, string>} pasted item lines, per section */
        _newItems: { state: true },
        /** @type {{id: string, value: string}|null} the section or item being renamed */
        _renaming: { state: true },
        _confirmingArchive: { state: true },
    };

    static styles = css`
        :host {
            display: block;
        }
        .wrap {
            max-width: 72rem;
            margin: 0 auto;
            padding: 2rem 1rem 4rem;
        }
        .item-row {
            gap: 0.5rem;
        }
        .item-text {
            flex: 1 1 18rem;
            min-width: 0;
        }
        .status {
            font-size: 0.8rem;
            white-space: nowrap;
        }
        .stale {
            color: var(--bs-warning-text-emphasis);
        }
        .outcomes .btn {
            --bs-btn-padding-y: 0.1rem;
            --bs-btn-padding-x: 0.45rem;
            --bs-btn-font-size: 0.75rem;
        }
        .issue-chip {
            font-size: 0.75rem;
            text-decoration: none;
        }
    `;

    constructor() {
        super();
        this.planId = '';
        this._plan = null;
        this._loading = true;
        this._error = '';
        this._filter = 'all';
        this._stalestFirst = false;
        this._editing = false;
        this._recording = null;
        this._note = '';
        this._build = '';
        this._justFailed = '';
        this._busy = false;
        this._actionError = '';
        this._newSection = '';
        this._newItems = {};
        this._renaming = null;
        this._confirmingArchive = false;
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        this._error = '';
        try {
            this._plan = await api.get(`/et/plans/${this.planId}`);
        } catch (error) {
            this._error = error.status === 404 ? 'No such plan.' : error.message;
        } finally {
            this._loading = false;
        }
    }

    /** Run a change, then reload the plan; a refusal shows above the plan. */
    async _act(change) {
        this._busy = true;
        this._actionError = '';
        try {
            await change();
            await this._load();
        } catch (error) {
            this._actionError = error.message;
        } finally {
            this._busy = false;
        }
    }

    _record(itemId) {
        const { outcome } = this._recording;
        return this._act(async () => {
            await api.post(`/et/items/${itemId}/results`, {
                outcome,
                note: this._note.trim(),
                build: this._build.trim(),
            });
            this._recording = null;
            this._note = '';
            // The build is kept: a tester usually records a run of items against it.
            this._justFailed = outcome === 'fail' || outcome === 'blocked' ? itemId : '';
        });
    }

    _addItems(sectionId) {
        const lines = splitItemLines(this._newItems[sectionId] ?? '');
        return this._act(async () => {
            // One at a time, in order: each goes on the end of the section.
            for (const text of lines) {
                await api.post(`/et/sections/${sectionId}/items`, { text });
            }
            this._newItems = { ...this._newItems, [sectionId]: '' };
        });
    }

    _move(path, ids, id, delta) {
        const order = moveId(ids, id, delta);
        if (!order) return;
        this._act(() => api.put(path, { ids: order }));
    }

    _rename(path) {
        const value = this._renaming.value.trim();
        const body = path.startsWith('/et/items') ? { text: value } : { name: value };
        return this._act(async () => {
            await api.put(path, body);
            this._renaming = null;
        });
    }

    render() {
        if (this._loading) return html`<div class="wrap"><p class="text-muted">Loading…</p></div>`;
        if (this._error) {
            return html`<div class="wrap">
                <div class="alert alert-danger" role="alert">${this._error}</div>
                <a href="/testing">All plans</a>
            </div>`;
        }

        const plan = this._plan;
        return html`
            <div class="wrap">
                <a class="small" href="/testing"><i class="bi bi-arrow-left"></i> All plans</a>
                <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
                    <h1 class="h4 mb-0">${plan.name}</h1>
                    <span class="text-muted small">issues go to ${plan.defaultRepo}</span>
                    <button
                        class="btn btn-sm ${this._editing ? 'btn-primary' : 'btn-outline-secondary'} ms-auto"
                        type="button"
                        @click=${() => {
                            this._editing = !this._editing;
                            this._renaming = null;
                        }}
                    >
                        <i class="bi bi-pencil"></i> ${this._editing ? 'Done editing' : 'Edit plan'}
                    </button>
                </div>
                ${plan.description ? html`<p class="text-muted mt-2 mb-0">${plan.description}</p>` : nothing}
                ${this._renderToolbar()}
                ${
                    this._actionError
                        ? html`<div class="alert alert-danger" role="alert">${this._actionError}</div>`
                        : nothing
                }
                ${plan.sections.map((section, index) => this._renderSection(section, index))}
                ${this._editing ? this._renderAddSection() : nothing}
                ${
                    plan.sections.length === 0 && !this._editing
                        ? html`<p class="text-muted">This plan is empty. Use <strong>Edit plan</strong> to add sections and items.</p>`
                        : nothing
                }
            </div>
        `;
    }

    _renderToolbar() {
        return html`
            <div class="d-flex flex-wrap align-items-center gap-2 my-3">
                <select
                    class="form-select form-select-sm w-auto"
                    aria-label="Show"
                    @change=${(e) => {
                        this._filter = e.target.value;
                    }}
                >
                    ${ITEM_FILTERS.map(
                        (f) =>
                            html`<option value=${f.value} ?selected=${f.value === this._filter}>${f.label}</option>`,
                    )}
                </select>
                <div class="form-check form-switch mb-0">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="stalest"
                        .checked=${this._stalestFirst}
                        @change=${(e) => {
                            this._stalestFirst = e.target.checked;
                        }}
                    />
                    <label class="form-check-label small" for="stalest">Stalest first</label>
                </div>
                <div class="input-group input-group-sm w-auto ms-auto">
                    <span class="input-group-text">Build</span>
                    <input
                        class="form-control"
                        placeholder="e.g. v0.31.2"
                        aria-label="Build you are testing"
                        .value=${this._build}
                        @input=${(e) => {
                            this._build = e.target.value;
                        }}
                    />
                </div>
            </div>
        `;
    }

    _renderSection(section, index) {
        const now = Date.now();
        let items = section.items.filter((item) => itemMatches(item, this._filter, now));
        if (this._stalestFirst) items = sortStalestFirst(items);
        if (items.length === 0 && this._filter !== 'all' && !this._editing) return nothing;

        const sectionIds = this._plan.sections.map((s) => s.id);
        const itemIds = section.items.map((i) => i.id);
        return html`
            <section class="card mb-3">
                <div class="card-header d-flex flex-wrap align-items-center gap-2">
                    ${
                        this._renaming?.id === section.id
                            ? this._renderRename(`/et/sections/${section.id}`, section.id)
                            : html`<h2 class="h6 mb-0">${section.name}</h2>`
                    }
                    ${this._renderClaim(section, now)}
                    ${
                        this._editing
                            ? html`<div class="btn-group btn-group-sm ms-auto">
                                  <button class="btn btn-outline-secondary" title="Move up" ?disabled=${index === 0 || this._busy}
                                      @click=${() => this._move(`/et/plans/${this.planId}/sections/order`, sectionIds, section.id, -1)}>
                                      <i class="bi bi-arrow-up"></i></button>
                                  <button class="btn btn-outline-secondary" title="Move down"
                                      ?disabled=${index === sectionIds.length - 1 || this._busy}
                                      @click=${() => this._move(`/et/plans/${this.planId}/sections/order`, sectionIds, section.id, 1)}>
                                      <i class="bi bi-arrow-down"></i></button>
                                  <button class="btn btn-outline-secondary" title="Rename"
                                      @click=${() => {
                                          this._renaming = { id: section.id, value: section.name };
                                      }}><i class="bi bi-pencil"></i></button>
                                  <button class="btn btn-outline-danger" title="Archive section" ?disabled=${this._busy}
                                      @click=${() => this._act(() => api.delete(`/et/sections/${section.id}`))}>
                                      <i class="bi bi-archive"></i></button>
                              </div>`
                            : nothing
                    }
                </div>
                <ul class="list-group list-group-flush">
                    ${items.map((item) => this._renderItem(item, itemIds, section.id, now))}
                    ${
                        items.length === 0
                            ? html`<li class="list-group-item text-muted small">No items${this._filter === 'all' ? '' : ' match'}.</li>`
                            : nothing
                    }
                </ul>
                ${this._editing ? this._renderAddItems(section.id) : nothing}
            </section>
        `;
    }

    _renderClaim(section, now) {
        const claim = isClaimActive(section.claim, now) ? section.claim : null;
        if (!claim) {
            return html`<button class="btn btn-sm btn-outline-primary" ?disabled=${this._busy}
                title="Tell others you're testing this section (4 hours)"
                @click=${() => this._act(() => api.post(`/et/sections/${section.id}/claim`))}>
                <i class="bi bi-flag"></i> Claim</button>`;
        }
        const mine = claim.by.subject === authState.user?.subject;
        const until = new Date(claim.expiresAt).toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
        });
        return html`
            <span class="badge ${mine ? 'text-bg-primary' : 'text-bg-info'}">
                <i class="bi bi-flag-fill"></i> ${mine ? 'You are testing this' : `${claim.by.name} is testing this`}
                ${claim.by.kind === 'agent' ? html`<i class="bi bi-robot" title="via an agent"></i>` : nothing}
                · until ${until}
            </span>
            ${
                mine
                    ? html`<button class="btn btn-sm btn-link p-0" ?disabled=${this._busy}
                          @click=${() => this._act(() => api.post(`/et/sections/${section.id}/claim`))}>Extend</button>`
                    : nothing
            }
            <button class="btn btn-sm btn-link p-0" ?disabled=${this._busy}
                @click=${() => this._act(() => api.delete(`/et/sections/${section.id}/claim`))}>Release</button>
        `;
    }

    _renderItem(item, itemIds, sectionId, now) {
        const recording = this._recording?.itemId === item.id;
        const index = itemIds.indexOf(item.id);
        return html`
            <li class="list-group-item">
                <div class="d-flex flex-wrap align-items-center item-row">
                    <div class="item-text">
                        ${
                            this._renaming?.id === item.id
                                ? this._renderRename(`/et/items/${item.id}`, item.id)
                                : html`<a href="/testing/items/${item.id}" class="link-body-emphasis text-decoration-none">${item.text}</a>`
                        }
                    </div>
                    ${this._renderStatus(item, now)}
                    ${item.issues.map(
                        (
                            issue,
                        ) => html`<a class="badge ${issue.state === 'open' ? 'text-bg-success' : 'text-bg-secondary'} issue-chip"
                            href=${issue.url} target="_blank" rel="noopener" title=${issue.title}>
                            ${issue.repo.replace('dice-driven/', '')}#${issue.number}</a>`,
                    )}
                    ${
                        item.noteCount > 0
                            ? html`<span class="text-muted small" title="${item.noteCount} notes"><i class="bi bi-chat-left-text"></i> ${item.noteCount}</span>`
                            : nothing
                    }
                    ${this._editing ? this._renderItemEdit(item, itemIds, sectionId, index) : this._renderOutcomeButtons(item)}
                </div>
                ${recording ? this._renderRecordForm(item) : nothing}
                ${
                    this._justFailed === item.id && !recording
                        ? html`<div class="small mt-1">
                              Recorded. <a href="/testing/items/${item.id}#issue">File an issue or link one →</a>
                          </div>`
                        : nothing
                }
            </li>
        `;
    }

    _renderStatus(item, now) {
        const last = item.lastResult;
        if (!last) return html`<span class="status text-muted">never tested</span>`;
        const outcome = outcomeOf(last.outcome);
        const stale = stalenessOf(item, now) === 'stale';
        return html`
            <span class="status ${stale ? 'stale' : 'text-muted'}" title=${last.note || ''}>
                <span class="badge ${outcome?.badge ?? 'text-bg-light'}">${outcome?.label ?? last.outcome}</span>
                ${timeAgo(last.createdAt, now)} · ${last.by.name}
                ${last.by.kind === 'agent' ? html`<i class="bi bi-robot" title="recorded by an agent"></i>` : nothing}
            </span>
        `;
    }

    _renderOutcomeButtons(item) {
        return html`
            <div class="btn-group outcomes" role="group" aria-label="Record a result">
                ${OUTCOMES.map(
                    (outcome) => html`<button
                        type="button"
                        class="btn ${this._recording?.itemId === item.id && this._recording.outcome === outcome.value ? outcome.button.replace('-outline', '') : outcome.button}"
                        ?disabled=${this._busy}
                        @click=${() => {
                            this._recording = { itemId: item.id, outcome: outcome.value };
                            this._justFailed = '';
                        }}
                    >
                        ${outcome.label}
                    </button>`,
                )}
            </div>
        `;
    }

    _renderRecordForm(item) {
        const outcome = outcomeOf(this._recording.outcome);
        return html`
            <form class="d-flex flex-wrap gap-2 mt-2"
                @submit=${(e) => {
                    e.preventDefault();
                    this._record(item.id);
                }}>
                <span class="badge ${outcome.badge} align-self-center">${outcome.label}</span>
                <input
                    class="form-control form-control-sm flex-grow-1 w-auto"
                    placeholder=${this._recording.outcome === 'pass' ? 'Note (optional)' : 'What happened?'}
                    aria-label="Note"
                    .value=${this._note}
                    @input=${(e) => {
                        this._note = e.target.value;
                    }}
                />
                <button class="btn btn-sm btn-primary" type="submit" ?disabled=${this._busy}>Record</button>
                <button class="btn btn-sm btn-outline-secondary" type="button"
                    @click=${() => {
                        this._recording = null;
                    }}>Cancel</button>
            </form>
        `;
    }

    _renderItemEdit(item, itemIds, sectionId, index) {
        return html`
            <div class="btn-group btn-group-sm">
                <button class="btn btn-outline-secondary" title="Move up" ?disabled=${index === 0 || this._busy}
                    @click=${() => this._move(`/et/sections/${sectionId}/items/order`, itemIds, item.id, -1)}>
                    <i class="bi bi-arrow-up"></i></button>
                <button class="btn btn-outline-secondary" title="Move down" ?disabled=${index === itemIds.length - 1 || this._busy}
                    @click=${() => this._move(`/et/sections/${sectionId}/items/order`, itemIds, item.id, 1)}>
                    <i class="bi bi-arrow-down"></i></button>
                <button class="btn btn-outline-secondary" title="Reword"
                    @click=${() => {
                        this._renaming = { id: item.id, value: item.text };
                    }}><i class="bi bi-pencil"></i></button>
                <button class="btn btn-outline-danger" title="Archive item" ?disabled=${this._busy}
                    @click=${() => this._act(() => api.delete(`/et/items/${item.id}`))}>
                    <i class="bi bi-archive"></i></button>
            </div>
        `;
    }

    _renderRename(path, id) {
        return html`
            <form class="d-flex gap-1 flex-grow-1"
                @submit=${(e) => {
                    e.preventDefault();
                    this._rename(path);
                }}>
                <input class="form-control form-control-sm" required aria-label="New text"
                    .value=${this._renaming.value}
                    @input=${(e) => {
                        this._renaming = { id, value: e.target.value };
                    }} />
                <button class="btn btn-sm btn-primary" type="submit" ?disabled=${this._busy}>Save</button>
                <button class="btn btn-sm btn-outline-secondary" type="button"
                    @click=${() => {
                        this._renaming = null;
                    }}>Cancel</button>
            </form>
        `;
    }

    _renderAddItems(sectionId) {
        const lines = splitItemLines(this._newItems[sectionId] ?? '');
        return html`
            <div class="card-body border-top">
                <textarea
                    class="form-control form-control-sm mb-2"
                    rows="3"
                    placeholder="One item per line — e.g. A GM can hide a miniature from players"
                    aria-label="New items"
                    .value=${this._newItems[sectionId] ?? ''}
                    @input=${(e) => {
                        this._newItems = { ...this._newItems, [sectionId]: e.target.value };
                    }}
                ></textarea>
                <button class="btn btn-sm btn-primary" type="button" ?disabled=${lines.length === 0 || this._busy}
                    @click=${() => this._addItems(sectionId)}>
                    Add ${lines.length === 1 ? 'item' : `${lines.length || ''} items`}
                </button>
            </div>
        `;
    }

    _renderAddSection() {
        return html`
            <form class="d-flex gap-2"
                @submit=${(e) => {
                    e.preventDefault();
                    this._act(async () => {
                        await api.post(`/et/plans/${this.planId}/sections`, {
                            name: this._newSection.trim(),
                        });
                        this._newSection = '';
                    });
                }}>
                <input class="form-control" required maxlength="100" placeholder="New section, e.g. Fog of war"
                    aria-label="New section name"
                    .value=${this._newSection}
                    @input=${(e) => {
                        this._newSection = e.target.value;
                    }} />
                <button class="btn btn-primary" type="submit" ?disabled=${this._busy}>Add section</button>
            </form>
            <div class="mt-4 d-flex align-items-center justify-content-end gap-2">
                ${
                    this._confirmingArchive
                        ? html`<span class="small">Archive “${this._plan.name}”? It leaves the plan list; its history is kept.</span>
                              <button class="btn btn-sm btn-danger" type="button" ?disabled=${this._busy}
                                  @click=${async () => {
                                      this._busy = true;
                                      try {
                                          await api.delete(`/et/plans/${this.planId}`);
                                          router.navigate('/testing');
                                      } catch (error) {
                                          this._actionError = error.message;
                                      } finally {
                                          this._busy = false;
                                      }
                                  }}>Archive</button>
                              <button class="btn btn-sm btn-outline-secondary" type="button"
                                  @click=${() => {
                                      this._confirmingArchive = false;
                                  }}>Cancel</button>`
                        : html`<button class="btn btn-sm btn-outline-danger" type="button"
                              @click=${() => {
                                  this._confirmingArchive = true;
                              }}><i class="bi bi-archive"></i> Archive this plan…</button>`
                }
            </div>
        `;
    }
}

customElements.define('d3-et-plan-view', ETPlanView);
